import { SERVICES, getStartingPrice } from '@/lib/pricing';

const SITE_URL = 'https://daleyinventories.com';
const LOGO_URL = 'https://media.base44.com/images/public/6a54fc37929928fd03a2219b/00729db5e_daley_logo_whitewriting.png';
const OG_IMAGE = 'https://media.base44.com/images/public/6a54fc37929928fd03a2219b/65aced434_generated_image.png';

const ORG = {
  '@type': 'LocalBusiness',
  '@id': SITE_URL,
  name: 'Daley Inventories Ltd',
  url: SITE_URL,
  telephone: '+442089355492',
  email: 'info@daleyinventories.com',
  image: OG_IMAGE,
  logo: LOGO_URL,
  priceRange: '££',
  foundingDate: '2016',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Canary Wharf',
    addressLocality: 'London',
    addressCountry: 'GB',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 51.5045,
    longitude: -0.0195,
  },
  areaServed: [
    { '@type': 'City', name: 'London' },
    { '@type': 'City', name: 'Manchester' },
    
  ],
  openingHoursSpecification: [{
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    opens: '09:00',
    closes: '18:00',
  }],
  memberOf: ['AIIC', 'NICEIC', 'Gas Safe Register', 'EPC Register'],
  sameAs: [
    'https://www.google.com/maps/place/Daley+Inventories+LTD/@51.5307909,-0.096204,17z',
    'https://www.yell.com/biz/daley-inventories-ltd-london-10344441/',
    'https://www.trustpilot.com/review/daleyinventories.com',
  ],
};

export function buildLocalBusinessSchema() {
  return {
    '@context': 'https://schema.org',
    ...ORG,
    description: 'Professional property inventory, safety certification, and cleaning services across London and Manchester. Over 10 years of experience with 20,000+ landlords served. AIIC, NICEIC, Gas Safe and EPC accredited.',
  };
}

export function buildServiceCatalogSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'OfferCatalog',
    name: 'Property Inventory & Safety Services',
    itemListElement: SERVICES.map(service => ({
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: service.name,
        description: service.description,
        provider: { '@type': 'LocalBusiness', name: 'Daley Inventories Ltd' },
      },
      price: getStartingPrice(service.id).toFixed(2),
      priceCurrency: 'GBP',
      availability: 'https://schema.org/InStock',
    })),
  };
}

export function buildAboutPageSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    mainEntity: {
      ...ORG,
      description: 'Founded in 2016, Daley Inventories has served over 20,000 landlords across London and Manchester with professional property inventory, safety certification, and cleaning services. Celebrating 10 years of trusted service in 2026.',
    },
  };
}

export function buildBreadcrumbSchema(path, name) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name, item: `${SITE_URL}${path}` },
    ],
  };
}

export function buildAggregateRatingSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Property Inventory Reports',
    description: 'Legally binding, photographic property inventory reports with 24–48 hour turnaround. AIIC-accredited clerks serving London and Manchester.',
    brand: { '@type': 'Brand', name: 'Daley Inventories Ltd' },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.3',
      reviewCount: '58',
      bestRating: '5',
      worstRating: '1',
    },
    offers: {
      '@type': 'Offer',
      price: '89.99',
      priceCurrency: 'GBP',
      availability: 'https://schema.org/InStock',
    },
  };
}

export function buildContactSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    mainEntity: {
      ...ORG,
      description: 'Contact Daley Inventories — available 7 days a week, 9am to 6pm. Call 0208 935 5492 or email info@daleyinventories.com.',
    },
  };
}

export function buildTestimonialsPageSchema(reviews = []) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Daley Inventories Property Inventory Services',
    description: 'Professional property inventory, safety certification, and cleaning services. Verified customer reviews.',
    brand: { '@type': 'Brand', name: 'Daley Inventories Ltd' },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      reviewCount: '58',
      bestRating: '5',
      worstRating: '1',
    },
    review: reviews.map(r => ({
      '@type': 'Review',
      author: { '@type': 'Person', name: r.name },
      datePublished: r.date,
      reviewRating: { '@type': 'Rating', ratingValue: r.rating, bestRating: 5 },
      reviewBody: r.text,
      publisher: { '@type': 'Organization', name: r.source },
    })),
  };
}

export function buildServiceAreasSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Daley Inventories Ltd',
    url: SITE_URL,
    telephone: '+442089355492',
    description: 'Property inventory, safety certification and cleaning services covering London and Manchester and their surrounding areas.',
    areaServed: [
      { '@type': 'City', name: 'London' },
      { '@type': 'City', name: 'Manchester' },
      
    ],
  };
}

export function buildBlogSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Daley Inventories Blog',
    description: 'Property management, inventory and landlord safety advice from Daley Inventories.',
    url: `${SITE_URL}/blog`,
    publisher: { '@type': 'Organization', name: 'Daley Inventories Ltd' },
  };
}

export function buildBlogPostingSchema(post) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    datePublished: post.published_date,
    author: { '@type': 'Organization', name: post.author || 'Daley Inventories' },
    publisher: { '@type': 'Organization', name: 'Daley Inventories Ltd' },
    articleSection: post.category,
    keywords: post.category,
    mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
  };
}

export function buildJobPostingSchema(job) {
  return {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: job.title,
    description: job.description,
    hiringOrganization: { '@type': 'Organization', name: 'Daley Inventories Ltd', sameAs: SITE_URL },
    jobLocation: { '@type': 'Place', address: { '@type': 'PostalAddress', addressLocality: job.location, addressCountry: 'GB' } },
    employmentType: 'FULL_TIME',
    datePosted: '2026-08-11',
  };
}